import { Link } from 'react-router-dom';
import { ArrowRightIcon, MessageCircleIcon } from 'lucide-react';
import { RoutineCard } from '../shared/RoutineCard';
import { ShopFaq } from '../shared/ShopFaq';
import { SubscriptionBlock } from '../shared/SubscriptionBlock';
import { pageActions } from '../../data/pageActions';
import { HeadingText } from '../shared/HeadingText';
import { PageHero } from '../shared/PageHero';
import { useCatalog } from '../../contexts/CatalogContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { founder, salon } from '../../data/content';
import { SALON_CODE } from '../../data/codes';
import { usePages } from '../../contexts/SiteContext';
import { fill } from '../../utils/copy';
import { mediaOr } from '../../core/rules/media';
import { shopLabels } from '../../data/shopLabels';
import { btnPrimaryLg, btnSecondaryLg } from '../../ui/styles';
import { quietLink, sectionHeading } from '../../ui/sections';
import { ClosingBand } from '../shared/ClosingBand';
import { plural } from '../../core/format/text';
import { RoutineCompare } from './RoutineCompare';
import { RoutineAnchor } from './RoutineAnchor';
import { GiftTierList } from './GiftTierList';
import { anchorRoutine, ROUTINES_FAQ_COUNT } from './routineRules';

/**
 * Protocollen: de pagina met de hoogste orderwaarde. Volgorde = de vragen van een bezoeker
 * (docs/VORMGEVING.md §3): wat is een protocol (hero, sand) · hoe werkt het en wat krijg ik
 * erbij (shell) · het complete protocol (blush) · of begin bij je klacht, en vergelijk (sand) ·
 * abonnement (kort) · vragen · al klant in de salon? (blush; de footer is klei).
 */
export function Routines() {
  const pages = usePages();
  const {
    openQuiz
  } = useOverlayActions();
  const {
    routines,
    routineTotals
  } = useCatalog();
  const copy = pages.routines;
  const complete = anchorRoutine(routines, r => routineTotals(r).price);
  const others = routines.filter(r => r.id !== complete?.id);
  const heroMedia = mediaOr(copy.hero.media, {
    image: complete?.image || founder.withProducts,
    alt: complete ? `De producten van ${complete.name} op een rij` : `${founder.name} met de producten waar ze mee werkt`
  });
  return <>
      {/* Hero: zelfde opbouw als salon en over. In het beeld: wie het opstelt, en wat je meekrijgt. */}
      <PageHero titleId="routines-title" eyebrow={copy.hero.eyebrow ? fill(copy.hero.eyebrow) : undefined} title={<HeadingText heading={copy.hero.heading} accentBlock data-id="element-8327" data-mp="25tyh75vnl18a" />} body={copy.hero.body ? fill(copy.hero.body) : undefined} media={heroMedia} byline={{
      lead: <>
              Persoonlijk huidplan, opgesteld door <span className="text-ink" data-id="element-8328" data-mp="2epnxj0i8v5et">{founder.firstName}</span>
            </>,
      sub: `${plural(routines.length, 'protocol', 'protocollen')} · 100% vegan`
    }} actions={/* Hoofdknop: naar de protocollen zelf. Wie nog twijfelt, doet eerst de huidtest. */
    <>
            <a href={complete ? '#anchor-routine' : '#other-routines'} className={btnPrimaryLg} data-id="element-8329" data-mp="hwz0lx0qdy5d">
              {pageActions.startProtocol}
            </a>
            {copy.hero.cta && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={btnSecondaryLg} data-id="element-8330" data-mp="7z2qn8jj598u">
                {fill(copy.hero.cta)}
              </button>}
          </>} data-id="element-8326" data-mp="20vn6ksxjamp0" />

      {/* Hoe het werkt: eerst uitleggen, dan pas aanbieden. Met de cadeaus ernaast. */}
      <section className="bg-shell py-20 sm:py-24" aria-labelledby="routines-how" data-id="element-8331" data-mp="vonarurf6cjp">
        <div className="mx-auto grid max-w-content gap-14 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8332" data-mp="vqq86zuwj7yg">
          <div className="lg:col-span-8" data-id="element-8333" data-mp="qvu8renc5ibb">
            <h2 id="routines-how" className="type-sub text-ink" data-id="element-8334" data-mp="lzlwqa4fq58d">
              {fill(copy.howHeading)}
            </h2>
            <ol className="mt-8 grid gap-8 sm:grid-cols-3 sm:gap-6" data-id="element-8335" data-mp="1jezsfxcgvnnh">
              {copy.steps.map((s, i) => <li key={s.title} className="border-t border-ink/15 pt-5" data-id="element-8336" data-mp="1ely5r360qjtc" data-mpi={i}>
                  {/* De lijst is al genummerd voor schermlezers; de volgorde is hier de inhoud. */}
                  <span aria-hidden="true" className="type-sub leading-none text-clay" data-id="element-8337" data-mp="145m7qt6ql9nn">
                    {i + 1}
                  </span>
                  <h3 className="mt-4 type-name text-ink" data-id="element-8338" data-mp="p9enxtz8qa8w">{fill(s.title)}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-muted" data-id="element-8339" data-mp="u6wzzt8k15l4">{fill(s.body)}</p>
                </li>)}
            </ol>
          </div>

          <div className="lg:col-span-4" data-id="element-8340" data-mp="1mvc3pjm8k1h5">
            <h2 className="type-sub text-ink" data-id="element-8341" data-mp="1muaqb8hqz2w9">{fill(copy.gifts.heading)}</h2>
            <GiftTierList className="mt-8" data-id="element-8342" data-mp="185ekzj72s3zc" />
            <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="mt-4 flex items-start gap-2 text-[14px] leading-relaxed text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-8343" data-mp="184bsx7y0nvhu">
              <MessageCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8344" data-mp="3qbphc6j1sgo1" />
              {fill(copy.gifts.note)}
              <span className="sr-only" data-id="element-8345" data-mp="3lccs5f4pexia">{shopLabels.viaWhatsApp}</span>
            </a>
          </div>
        </div>
      </section>

      {complete && <RoutineAnchor routine={complete} data-id="element-8346" data-mp="3v2xrj4s7c629" />}

      {/* Of begin bij je klacht. Met maar één protocol online is er geen "overige": dan alleen vergelijken (vanaf twee). */}
      {(others.length > 0 || routines.length > 1) && <section className="bg-sand py-20 sm:py-24 lg:py-32" aria-labelledby={others.length > 0 ? 'other-routines' : undefined} data-id="element-8347" data-mp="32f1ws5m70o84">
          <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-8348" data-mp="3gf4bjiz7f1j5">
            {others.length > 0 && <>
                <div className="grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-16" data-id="element-8349" data-mp="3cekd8c2w9rvf">
                  <h2 id="other-routines" className={`${sectionHeading} text-ink lg:col-span-7`} data-id="element-8350" data-mp="vpnb52a9ut3r">
                    <HeadingText heading={copy.others.heading} data-id="element-8351" data-mp="ro1qgurhxaj0" />
                  </h2>
                  {copy.others.body && <p className="max-w-md text-[17px] leading-relaxed text-muted lg:col-span-5" data-id="element-8352" data-mp="3kijwg2c1b7ef">{fill(copy.others.body)}</p>}
                </div>

                <ul className={`mt-14 grid gap-6 md:grid-cols-2 ${others.length >= 3 ? 'lg:grid-cols-3' : ''}`} data-id="element-8353" data-mp="3uedf83gcjx4n">
                  {others.map(r => <li key={r.id} className="h-full bg-shell p-7 lg:p-8" data-id="element-8354" data-mp="28hypvy4hpei">
                      <RoutineCard routine={r} data-id="element-8355" data-mp="31ritl37smrpl" />
                    </li>)}
                </ul>
              </>}

            <RoutineCompare heading={fill(copy.compareHeading)} data-id="element-8356" data-mp="3aqw84aary6h6" />

            {copy.smallStart.body && <div className="mt-12 flex max-w-3xl flex-wrap items-baseline gap-x-6 gap-y-3" data-id="element-8357" data-mp="3fhw4nefzemj5">
                <p className="text-[17px] leading-relaxed text-muted" data-id="element-8358" data-mp="2izq61obhtyp9">{fill(copy.smallStart.body)}</p>
                {copy.smallStart.shopCta && <Link to="/webshop" className={`${quietLink} text-ink`} data-id="element-8359" data-mp="2mzibhs5lboqe">
                    {fill(copy.smallStart.shopCta)}
                    <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8360" data-mp="2ff6cdubhztn0" />
                  </Link>}
                {copy.smallStart.quizCta && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={`${quietLink} text-ink underline decoration-ink/25 hover:decoration-ink`} data-id="element-8361" data-mp="26iyucnjh6j16">
                    {fill(copy.smallStart.quizCta)}
                  </button>}
              </div>}
          </div>
        </section>}

      {/* Abonnement en vragen: dezelfde blokken als op de webshop, hier in de korte versie. */}
      <SubscriptionBlock size="short" data-id="element-8362" data-mp="1irjnqiko1fk4" />

      <ShopFaq limit={ROUTINES_FAQ_COUNT} data-id="element-8363" data-mp="1isl1wvwzai62" />

      {/* Voor klanten van de salon: de salon als bron, op blush. Niet op klei: de footer is al klei. */}
      <ClosingBand tone="blush" titleId="salon-client" title={<HeadingText heading={copy.salonClient.heading} data-id="element-8365" data-mp="18vg9wfggbshc" />} body={copy.salonClient.body ? fill(copy.salonClient.body, {
      code: SALON_CODE
    }) : undefined} actions={copy.salonClient.cta && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={btnPrimaryLg} data-id="element-8366" data-mp="2npdkllpvuixp">
              {fill(copy.salonClient.cta)}
              <span className="sr-only" data-id="element-8367" data-mp="2smvk6li0flt1">{shopLabels.viaWhatsApp}</span>
            </a>} data-id="element-8364" data-mp="18tdm822rubfs" />
    </>;
}